/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { reducerLogger } from "../../../../utility/reducerLogger";
import { v4 as uuid } from "uuid";

export const statusAlertsActions = {
	AGENT_STATUS_LIST_GET: "AGENT_STATUS_LIST_GET",
	AGENT_STATUS_LIST_GET_SUCCESS: "AGENT_STATUS_LIST_GET_SUCCESS",
	AGENT_STATUS_LIST_GET_ERROR: "AGENT_STATUS_LIST_GET_ERROR",

	STATUS_ALERTS_LIST_GET: "STATUS_ALERTS_LIST_GET",
	STATUS_ALERTS_LIST_GET_SUCCESS: "STATUS_ALERTS_LIST_GET_SUCCESS",
	STATUS_ALERTS_LIST_GET_ERROR: "STATUS_ALERTS_LIST_GET_ERROR",

	STATUS_ALERTS_LIST_CLEAR_CHANGES: "STATUS_ALERTS_LIST_CLEAR_CHANGES",
	STATUS_ALERTS_LIST_UPDATE_CHANGES: "STATUS_ALERTS_LIST_UPDATE_CHANGES",

	STATUS_ALERT_ADD_NEW: "STATUS_ALERT_ADD_NEW",
	STATUS_ALERT_UPDATE: "STATUS_ALERT_UPDATE",
	STATUS_ALERT_REMOVE: "STATUS_ALERT_REMOVE",

	STATUS_ALERTS_LIST_UPDATE: "STATUS_ALERTS_LIST_UPDATE",
	STATUS_ALERTS_LIST_UPDATE_SUCCESS: "STATUS_ALERTS_LIST_UPDATE_SUCCESS",
	STATUS_ALERTS_LIST_UPDATE_ERROR: "STATUS_ALERTS_LIST_UPDATE_ERROR",
};

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

export const statusAlertsInitialState = {
	statusLookup: [],
	statusAlerts: [],
	statusAlertsBackup: [],
	loading: false,
	loaded: { statusAlerts: false, statusLookup: false },
	error: "",
	message: "",
};

export const newStatusAlert = {
	id: uuid(),
	alerts: [{ id: uuid(), time: "", message: "" }],
	statusName: "",
	active: true,
	activity: "NEW", // Used for user activity detection and form change detection.
};

const sortStatusAlerts = (statusAlerts) => {
	statusAlerts?.sort((prevSts, curSts) => (prevSts.statusName < curSts.statusName ? -1 : 1));
	return statusAlerts?.map((stAlert) => ({
		...stAlert,
		alerts: stAlert.alerts.sort((a, b) => (a.time > b.time ? 1 : -1)),
	}));
};

export const statusAlertsReducer = reducerLogger((state, action) => {
	let newState = { ...state };
	let statusAlerts;
	switch (action.type) {
		// Fetching agent statuses from AWS Connect API
		case statusAlertsActions.AGENT_STATUS_LIST_GET:
			newState = { ...state, statusLookup: [], loading: true, error: "", message: "" };
			return newState;
		case statusAlertsActions.AGENT_STATUS_LIST_GET_SUCCESS:
			const statusList = action.payload?.map((sts) => ({ ...sts, statusName: sts.Name })) || [];
			statusList.sort((prevSts, curSts) => (prevSts.statusName < curSts.statusName ? -1 : 1));
			newState = {
				...state,
				statusLookup: statusList,
				loading: false,
				error: "",
				message: "",
				loaded: { ...state.loaded, statusLookup: true },
			};
			return newState;
		case statusAlertsActions.AGENT_STATUS_LIST_GET_ERROR:
			newState = { ...state, statusLookup: [], loading: false, error: action.error, message: "" };
			return newState;

		// Fetching already configured Status alerts from database.
		case statusAlertsActions.STATUS_ALERTS_LIST_GET:
			newState = { ...state, statusAlerts: [], loading: true, error: "", statusAlertsBackup: [], message: "" };
			return newState;
		case statusAlertsActions.STATUS_ALERTS_LIST_GET_SUCCESS:
			statusAlerts = sortStatusAlerts(action.payload);
			newState = {
				...state,
				statusAlerts,
				statusAlertsBackup: deepCopy(statusAlerts),
				loading: false,
				loaded: { ...state.loaded, statusAlerts: true },
				error: "",
				message: "",
			};
			return newState;
		case statusAlertsActions.STATUS_ALERTS_LIST_GET_ERROR:
			newState = {
				...state,
				statusAlerts: [],
				loading: false,
				error: action.error,
				statusAlertsBackup: [],
				message: "",
			};
			return newState;

		//Clearing changes from UI.
		case statusAlertsActions.STATUS_ALERTS_LIST_CLEAR_CHANGES:
			newState = {
				...state,
				statusAlerts: deepCopy(state.statusAlertsBackup),
				loading: false,
				error: "",
				message: "",
			};
			return newState;

		//Handling UI updates on state.
		case statusAlertsActions.STATUS_ALERTS_LIST_UPDATE_CHANGES:
			newState = { ...state, statusAlerts: action.payload, loading: false, error: "" };
			return newState;

		//removing status alert from UI.
		case statusAlertsActions.STATUS_ALERT_REMOVE:
			statusAlerts = state.statusAlerts.filter((sts) => sts.id !== action.payload);
			newState = {
				...state,
				statusAlerts,
				statusAlertsBackup: deepCopy(statusAlerts),
				loading: false,
				error: "",
				message: "Successfully removed status alert.",
			};
			return newState;

		// Handling UI Changes for a single status.
		case statusAlertsActions.STATUS_ALERT_UPDATE:
			statusAlerts = state.statusAlerts || [];
			const updatedStatusAlert = { ...action.payload, activity: "UPDATE" };
			const updatedStatusAlerts = statusAlerts.map((sts) =>
				sts.id === updatedStatusAlert.id ? updatedStatusAlert : sts,
			);
			newState = { ...state, statusAlerts: updatedStatusAlerts };
			return newState;

		//Adding new Status alert
		case statusAlertsActions.STATUS_ALERT_ADD_NEW:
			statusAlerts = state.statusAlerts || [];
			statusAlerts = [action.payload, ...statusAlerts];
			newState = { ...state, statusAlerts };
			return newState;

		// Handle remote Database update calls.
		case statusAlertsActions.STATUS_ALERTS_LIST_UPDATE:
			newState = { ...state, loading: true, error: "", message: "" };
			return newState;
		case statusAlertsActions.STATUS_ALERTS_LIST_UPDATE_SUCCESS: // reset form changes to newly saved status alerts.
			statusAlerts = state.statusAlerts.map((sts) => {
				delete sts.activity;
				delete sts.new;
				return sts;
			});
			statusAlerts = sortStatusAlerts(statusAlerts);
			newState = {
				...state,
				statusAlerts,
				loading: false,
				error: "",
				statusAlertsBackup: deepCopy(state.statusAlerts),
				message: `Successfully ${action?.isNewStatusAlert ? "created" : "saved"} status alerts.`,
			};
			return newState;
		case statusAlertsActions.STATUS_ALERTS_LIST_UPDATE_ERROR:
			newState = { ...state, loading: false, error: action.error, message: "" };
			return newState;
		default:
			return state;
	}
});
